/*
 * Title: Reconcile In Bulk - Index
 * Description: Main layout with warehouse filter, search, and item list
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Row, Spinner, Table } from 'react-bootstrap';
import { useReconcileInBulk } from './ContextAPI';
import ItemRow from './ItemRow';

export default function Index() {
    const {
        bulkData,
        loading,
        showResults,
        filterType,
        warehouseId,
        setWarehouseId,
        includeZero,
        setIncludeZero,
        warehouses,
        filteredItems,
        rowStatuses,
        fetchBulkData,
        fetchWarehouses,
        clearResults,
        setFilterType,
        exportExcel,
        importExcel,
    } = useReconcileInBulk();

    const [search, setSearch] = useState('');
    const [localFilter, setLocalFilter] = useState(filterType);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchWarehouses();
    }, [fetchWarehouses]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchBulkData(warehouseId, debouncedSearch, includeZero);
    }, [fetchBulkData, warehouseId, debouncedSearch, includeZero]);

    const typeButtons = useMemo(
        () => [
            { key: 'all', label: 'All', color: 'secondary', count: bulkData?.length || 0 },
            { key: 'gain', label: 'Gain', color: 'success', count: bulkData?.filter(d => rowStatuses[d.detail_id]?.type === 'gain').length || 0 },
            { key: 'loss', label: 'Loss', color: 'danger', count: bulkData?.filter(d => rowStatuses[d.detail_id]?.type === 'loss').length || 0 },
            { key: 'equal', label: 'Equal', color: 'info', count: bulkData?.filter(d => (rowStatuses[d.detail_id]?.type || 'equal') === 'equal').length || 0 },
        ],
        [bulkData, rowStatuses]
    );

    const handleWarehouseChange = (e) => {
        setWarehouseId(parseInt(e.target.value, 10));
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
    };

    const handleFilterChange = (type) => {
        setLocalFilter(type);
        setFilterType(type);
    };

    const handleFileImport = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            importExcel(file);
            e.target.value = '';
        }
    };

    return (
        <Card>
            <Card.Header className="cardHeader d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h3 className="mb-0">Reconcile In Bulk</h3>
                <div className="d-flex gap-2 flex-wrap">
                    <Form.Select
                        value={warehouseId}
                        onChange={handleWarehouseChange}
                        className="w-auto"
                        style={{ minWidth: '200px' }}
                    >
                        {warehouses.map(w => (
                            <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                    </Form.Select>
                    <InputGroup size="sm" style={{ width: '300px' }}>
                        <InputGroup.Text>Search</InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Code, Name, PO, Batch..."
                            value={search}
                            onChange={handleSearch}
                        />
                    </InputGroup>
                    <Form.Check
                        type="switch"
                        id="include-zero-stock"
                        className="align-self-center mb-0"
                        label="Show zero-stock batches"
                        checked={includeZero}
                        onChange={(e) => setIncludeZero(e.target.checked)}
                    />
                    {showResults && (
                        <>
                            <Button variant="outline-success" size="sm" onClick={exportExcel}>
                                Export Excel
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()}>
                                Import Excel
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.csv"
                                className="d-none"
                                onChange={handleFileImport}
                            />
                        </>
                    )}
                    <Button
                        variant="outline-secondary"
                        onClick={clearResults}
                        disabled={!loading && !showResults}
                    >
                        Clear
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {loading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <h5 className="mt-3 text-muted">Loading stock data...</h5>
                    </div>
                )}

                {!loading && !showResults && (
                    <div className="text-center py-5">
                        <h5 className="text-muted">Select warehouse and data will load automatically</h5>
                    </div>
                )}

                {showResults && (
                    <>
                        <Row className="mb-4">
                            <Col lg={3}>
                                <Card className="text-center border">
                                    <Card.Body className="py-2">
                                        <small className="text-muted d-block">Total Items</small>
                                        <strong className="fs-4">{bulkData?.length || 0}</strong>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3}>
                                <Card className="text-center border">
                                    <Card.Body className="py-2">
                                        <small className="text-muted d-block">Gain</small>
                                        <strong className="fs-4 text-success">
                                            {bulkData?.filter(d => rowStatuses[d.detail_id]?.type === 'gain').length || 0}
                                        </strong>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3}>
                                <Card className="text-center border">
                                    <Card.Body className="py-2">
                                        <small className="text-muted d-block">Loss</small>
                                        <strong className="fs-4 text-danger">
                                            {bulkData?.filter(d => rowStatuses[d.detail_id]?.type === 'loss').length || 0}
                                        </strong>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3}>
                                <Card className="text-center border">
                                    <Card.Body className="py-2">
                                        <small className="text-muted d-block">Equal</small>
                                        <strong className="fs-4 text-info">
                                            {bulkData?.filter(d => (rowStatuses[d.detail_id]?.type || 'equal') === 'equal').length || 0}
                                        </strong>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <div className="mb-3 d-flex gap-2 flex-wrap">
                            {typeButtons.map((btn) => (
                                <Button
                                    key={btn.key}
                                    variant={localFilter === btn.key ? btn.color : `outline-${btn.color}`}
                                    size="sm"
                                    onClick={() => handleFilterChange(btn.key)}
                                >
                                    {btn.label} ({btn.count || 0})
                                </Button>
                            ))}
                        </div>

                        <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
                            <Table striped hover bordered size="sm" style={{ tableLayout: 'fixed', width: '100%' }}>
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th style={{ width: '50px' }}>#</th>
                                        <th>Code</th>
                                        <th>Drug Name</th>
                                        <th>PO No</th>
                                        <th style={{ width: '120px' }}>Batch</th>
                                        <th className="text-end" style={{ width: '100px' }}>Receive Qty</th>
                                        <th className="text-end" style={{ width: '120px' }}>Available Qty</th>
                                        <th className="text-end" style={{ width: '100px' }}>Dispatch Qty</th>
                                        <th style={{ width: '240px' }}>Expected Qty / Status</th>
                                        <th style={{ width: '120px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="text-center text-muted py-4">
                                                No items match the current filter.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredItems.map((item, index) => (
                                            <ItemRow key={`${item.detail_id}`} item={item} index={index} />
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}